/**
 * PATH: packages/bills/src/components/pdfTextGeometry.ts
 * FILE_ID: BILLS-PKG-CMP-PDFTEXTGEO-001
 * PURPOSE: Pure geometry for "pick a value off the PDF" — extract a page's text
 *          runs (normalised bboxes) from pdf.js and hit-test points / rects
 *          against them. No React, no DOM events, so the maths is unit-testable
 *          in isolation (the pick feature recurred 5×; the bug was always the
 *          wiring, never these functions).
 *
 * FLOW:
 *   page.getTextContent() ─▶ TextBox[] (0..1 bboxes)
 *   point  ─▶ boxAtPoint / pointOnTextRow
 *   rect   ─▶ boxesInRect / joinBoxesInRect
 *
 * DEPENDENCIES:
 *   - pdfjs-dist (legacy build): Util.transform for text→viewport mapping.
 * RELATED:
 *   - pdfTextCapture.tsx (CapturePageLayer): the React overlay that uses these.
 */

import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';

/** A single text run, bbox normalised 0..1 of the page (origin top-left). */
export interface TextBox {
  str: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** A normalised selection rectangle (0..1), x0<x1, y0<y1. */
export interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

interface ViewportLike {
  transform: number[];
  width: number;
  height: number;
}
interface TextItemLike {
  str: string;
  transform: number[];
  width: number;
  height: number;
}
interface PageLike {
  getTextContent: () => Promise<{ items: unknown[] }>;
}

export const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/**
 * Vertical extents of a text run around its BASELINE, as fractions of the
 * font size. pdf.js's transform gives us the baseline y (tr[5]) and the em
 * size, not the real font metrics — a typical Latin face has ~0.75–0.8em of
 * ascent and ~0.2em of descent. The earlier box spanned [baseline − 1em,
 * baseline], which floated the highlight too high and CLIPPED every
 * descender (the tails of g/y/Q sat outside the box — operator 2026-07-24
 * "seems clipping the text"). Slightly generous on both sides so the ring
 * never cuts through glyphs.
 */
const ASCENT_RATIO = 0.86;
const DESCENT_RATIO = 0.24;

/**
 * f(page, baseViewport@scale1) → normalised text boxes. Best-effort: a
 * scanned (image-only) PDF yields [] and the caller simply shows no boxes.
 */
export async function extractTextBoxes(
  page: PageLike,
  viewport: ViewportLike,
): Promise<TextBox[]> {
  let content: { items: unknown[] };
  try {
    content = await page.getTextContent();
  } catch {
    return [];
  }
  const vw = viewport.width || 1;
  const vh = viewport.height || 1;
  const boxes: TextBox[] = [];
  for (const raw of content.items) {
    const it = raw as Partial<TextItemLike>;
    if (typeof it.str !== 'string' || !it.str.trim() || !Array.isArray(it.transform)) {
      continue;
    }
    const tr = pdfjsLib.Util.transform(viewport.transform, it.transform);
    const fontHeight = Math.hypot(tr[2], tr[3]) || Math.abs(tr[3]) || 1;
    const width = it.width ?? 0;
    const left = tr[4];
    const baseline = tr[5];
    const x0 = clamp01(left / vw);
    const y0 = clamp01((baseline - fontHeight * ASCENT_RATIO) / vh);
    const x1 = clamp01((left + width) / vw);
    const y1 = clamp01((baseline + fontHeight * DESCENT_RATIO) / vh);
    if (x1 - x0 <= 0 || y1 - y0 <= 0) continue;
    boxes.push({ str: it.str, x0, y0, x1, y1 });
  }
  return boxes;
}

const LINE_TOL = 0.012;

/** The boxes a selection rect covers, sorted in reading order (row, then x). */
export function boxesInRect(boxes: TextBox[], rect: Rect): TextBox[] {
  const hit = boxes.filter(
    (b) => b.x0 < rect.x1 && b.x1 > rect.x0 && b.y0 < rect.y1 && b.y1 > rect.y0,
  );
  hit.sort((a, b) => {
    const la = Math.round(a.y0 / LINE_TOL);
    const lb = Math.round(b.y0 / LINE_TOL);
    if (la !== lb) return la - lb;
    return a.x0 - b.x0;
  });
  return hit;
}

/** Join the boxes inside a selection rect in reading order (row, then x). */
export function joinBoxesInRect(boxes: TextBox[], rect: Rect): string {
  return boxesInRect(boxes, rect)
    .map((b) => b.str)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** f(ax,ay,bx,by) → a normalised rect with x0<x1, y0<y1 from two corners. */
export function rectFromPoints(ax: number, ay: number, bx: number, by: number): Rect {
  return {
    x0: Math.min(ax, bx),
    y0: Math.min(ay, by),
    x1: Math.max(ax, bx),
    y1: Math.max(ay, by),
  };
}

/**
 * f(point) → is the press within a LINE of text (so it should start a text
 * selection), vs the blank margins (so it should pan)? True when the y falls in
 * any run's vertical band AND the x is between that line's first and last run —
 * forgiving of the gaps between words, so "just drag over it" works, while the
 * margins above/below/left/right of the text still pan a zoomed page.
 */
export function pointOnTextRow(boxes: TextBox[], px: number, py: number): boolean {
  let lo = Infinity;
  let hi = -Infinity;
  for (const b of boxes) {
    if (py >= b.y0 && py <= b.y1) {
      if (b.x0 < lo) lo = b.x0;
      if (b.x1 > hi) hi = b.x1;
    }
  }
  return hi >= lo && px >= lo && px <= hi;
}

/** Smallest box under a point (most specific run wins overlaps). */
export function boxAtPoint(boxes: TextBox[], px: number, py: number): TextBox | null {
  let best: TextBox | null = null;
  let bestArea = Infinity;
  for (const b of boxes) {
    if (px >= b.x0 && px <= b.x1 && py >= b.y0 && py <= b.y1) {
      const area = (b.x1 - b.x0) * (b.y1 - b.y0);
      if (area < bestArea) {
        bestArea = area;
        best = b;
      }
    }
  }
  return best;
}
